// MarkReport.jsx

import { useEffect, useMemo, useState } from "react";
import { SweetAlertShow } from "../../../master/Master";
import { studentDetailsMarkReport } from "../../../../api/StudentDetails";
import { printImg } from "../../../master/Print";
import usePrivateLink from "../../../config/usePrivateLink";

/* ---------- helpers ---------- */

const toNum = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};

const getGrade = (pct, grading = []) => {
    if (pct === null || pct === undefined) return "-";

    if (Array.isArray(grading) && grading.length > 0) {
        const percentage = Number(pct);

        const matchedGrade = grading.find((item) => {
            const from = Number(item.from_percentage);
            const to = Number(item.to_percentage);

            return percentage >= from && percentage <= to;
        });

        if (matchedGrade) {
            return matchedGrade.grade;
        }

        return "F";
    }

    // if (pct >= 90) return "A+";
    // if (pct >= 80) return "A";
    // if (pct >= 70) return "B+";
    // if (pct >= 60) return "B";
    // if (pct >= 50) return "C";
    // if (pct >= 40) return "D";

    return "F";
};
const gradeColor = (grade) => {
    if (!grade || grade === "-") return "secondary";

    const value = String(grade).trim().toUpperCase();
    if (
        value === "F" ||
        value === "FAIL" ||
        value === "FAILED"
    ) {
        return "danger";
    }
    if (
        value === "D" ||
        value === "C" ||
        value === "POOR"
    ) {
        return "warning";
    }
    return "success";
};

const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return d;
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

// Resolve the single "mark" record + status for a test
const getTestResult = (test) => {
    const markRow = test?.marks?.[0];
    const totalMarks = toNum(test?.total_marks);

    if (!markRow) {
        return {
            status: "pending",
            obtained: null,
            totalMarks,
            pct: null,
            rank: null,
        };
    }
    if (markRow.is_absent === "1") {
        return {
            status: "absent",
            obtained: null,
            totalMarks,
            pct: null,
            rank: markRow.rank ?? null,
        };
    }
    const obtained = toNum(markRow.obtained_total);
    if (test?.is_marks_locked === "0" || obtained === null) {
        return {
            status: "pending",
            obtained: null,
            totalMarks,
            pct: null,
            rank: null,
        };
    }
    const pct = totalMarks ? (obtained / totalMarks) * 100 : null;
    return {
        status: "evaluated",
        obtained,
        totalMarks,
        pct,
        rank: markRow.rank ?? null,
    };
};

/* ---------- skeleton / empty states ---------- */

function SummarySkeleton() {
    return (
        <>
            {[0, 1, 2, 3].map((i) => (
                <div className="col-lg-3 col-md-6" key={i}>
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <span className="placeholder-glow d-block mb-2">
                                <span className="placeholder col-6"></span>
                            </span>
                            <span className="placeholder-glow d-block">
                                <span
                                    className="placeholder col-4"
                                    style={{ height: 28 }}
                                ></span>
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
}

function BatchSkeleton() {
    return (
        <div className="col-12">
            <div className="card shadow-sm border-0">
                <div className="card-header bg-white">
                    <span className="placeholder-glow d-block">
                        <span className="placeholder col-3"></span>
                    </span>
                </div>
                <div className="card-body">
                    {[0, 1, 2].map((i) => (
                        <span className="placeholder-glow d-block mb-2" key={i}>
                            <span
                                className="placeholder col-12"
                                style={{ height: 18 }}
                            ></span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

function EmptyState({ icon, title, subtitle }) {
    return (
        <div className="col-12">
            <div className="card shadow-sm border-0">
                <div className="card-body text-center py-5">
                    <i className={`${icon} text-muted`} style={{ fontSize: 42 }}></i>
                    <h6 className="mt-3 mb-1">{title}</h6>
                    <p className="text-muted small mb-0">{subtitle}</p>
                </div>
            </div>
        </div>
    );
}

/* ---------- status badge ---------- */

function ResultBadge({ status }) {
    if (status === "pending")
        return (
            <span className="badge bg-secondary-subtle text-secondary">
                <i className="ri-time-line me-1"></i>Pending
            </span>
        );
    if (status === "absent")
        return (
            <span className="badge bg-danger-subtle text-danger">
                <i className="ri-close-circle-line me-1"></i>Absent
            </span>
        );
    return (
        <span className="badge bg-success-subtle text-success">
            <i className="ri-checkbox-circle-line me-1"></i>Pass
        </span>
    );
}

/* ---------- one test row + expandable subject breakdown ---------- */

function TestRow({ test, isOpen, onToggle, grading }) {
    const { status, obtained, totalMarks, pct, rank } = getTestResult(test);
    // const grade = pct !== null ? getGrade(pct, grading) : "-";
    const grade = status === "absent" ? getGrade(0, grading) : pct !== null ? getGrade(pct, grading) : "-";

    return (
        <>
            <tr
                role="button"
                onClick={() => onToggle(test.id)}
                className={isOpen ? "table-active" : ""}
            >
                <td>
                    <i
                        className={`ri-arrow-${isOpen ? "down" : "right"}-s-line me-1 text-muted`}
                    ></i>
                    {test.title}
                </td>
                <td>{formatDate(test.date)}</td>
                <td>{totalMarks ?? "-"}</td>
                <td>{obtained ?? "-"}</td>
                <td>{pct !== null ? `${pct.toFixed(1)}%` : "-"}</td>
                <td>
                    {grade !== "-" ? (
                        <span
                            className={`badge bg-${gradeColor(grade)}-subtle text-${gradeColor(grade)}`}
                        >
                            {grade}
                        </span>
                    ) : (
                        "-"
                    )}
                </td>
                <td>{rank ?? "-"}</td>
                <td>
                    <ResultBadge status={status} />
                </td>
            </tr>

            {isOpen && (
                <tr>
                    <td colSpan={8} className="bg-light-subtle p-0">
                        <div className="p-3">
                            {test?.subjects_mark?.length ? (
                                <div className="row g-2">
                                    {test.subjects_mark.map((s) => {
                                        const sObtained = toNum(s.obtained_marks);
                                        const sTotal = toNum(s.total_marks);

                                        const sPct =
                                            status === "evaluated" &&
                                                sObtained !== null &&
                                                sTotal
                                                ? (sObtained / sTotal) * 100
                                                : null;

                                        const subjectGrade =
                                            status === "absent"
                                                ? getGrade(0, grading)
                                                : sPct !== null
                                                    ? getGrade(sPct, grading)
                                                    : "-";

                                        return (
                                            <div className="col-md-3 col-6" key={s.subject_id}>
                                                <div className="border rounded p-2 h-100 bg-white">
                                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                                        <small className="text-muted">
                                                            <i className="ri-book-2-line me-1"></i>
                                                            {s.subject_name}
                                                        </small>

                                                        {subjectGrade !== "-" && (
                                                            <span
                                                                className={`badge bg-${gradeColor(subjectGrade)}-subtle text-${gradeColor(subjectGrade)}`}
                                                            >
                                                                {subjectGrade}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <strong>
                                                        {status === "evaluated" && sObtained !== null
                                                            ? sObtained
                                                            : "-"}{" "}
                                                        <span className="text-muted fw-normal">
                                                            / {s.total_marks}
                                                        </span>
                                                    </strong>

                                                    {sPct !== null && (
                                                        <div
                                                            className="progress mt-1"
                                                            style={{ height: 4 }}
                                                        >
                                                            <div
                                                                className={`progress-bar bg-${gradeColor(
                                                                    getGrade(sPct, grading)
                                                                )}`}
                                                                style={{
                                                                    width: `${Math.min(sPct, 100)}%`,
                                                                }}
                                                            ></div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-muted small mb-0">
                                    No subject-wise breakdown available for this test.
                                </p>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

/* ---------- one batch/course/session block ---------- */

function BatchCard({ entry, openTestId, onToggleTest, grading, data }) {
    const tests = entry?.tests ?? [];
    const hasAccess = usePrivateLink();

    const evaluated = tests
        .map((t) => getTestResult(t))
        .filter((r) => r.status === "evaluated");

    const sumObtained = evaluated.reduce((a, r) => a + (r.obtained ?? 0), 0);
    const sumTotal = evaluated.reduce((a, r) => a + (r.totalMarks ?? 0), 0);
    const avgPct = sumTotal ? (sumObtained / sumTotal) * 100 : null;
    const latestRank =
        [...evaluated].reverse().find((r) => r.rank !== null)?.rank ?? null;

    const feeStatusColor =
        entry?.fee_status === "Paid"
            ? "success"
            : entry?.fee_status === "Due"
                ? "warning"
                : "secondary";

    return (
        <div className="col-12 " id="PrintDiv">
            <div className="card shadow-sm border-0">
                <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                        <h6 className="mb-1">
                            <i className="ri-team-line me-1 text-primary"></i>
                            {entry?.batch?.title ?? "-"}
                            &nbsp;•&nbsp;
                            <i className="ri-graduation-cap-line text-primary me-2"></i>
                            {entry?.course?.title ?? "Course"}



                        </h6>
                        <small className="text-muted">
                            <i className="ri-user-line me-1"></i>
                            {data?.name ?? "-"}

                            &nbsp;•&nbsp;

                            <i className="ri-file-list-3-line me-1"></i>
                            Reg No. {data?.registration_no ?? "-"}

                            &nbsp;•&nbsp;

                            <i className="ri-calendar-2-line me-1"></i>
                            {entry?.session_year?.session ?? "-"}

                            &nbsp;•&nbsp;



                            <i className="ri-hashtag me-1"></i>
                            Roll No. {entry?.roll_number ?? "-"}
                        </small>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <span
                            className={`badge bg-${feeStatusColor}-subtle text-${feeStatusColor}`}
                        >
                            <i className="ri-money-rupee-circle-line me-1"></i>
                            Fee {entry?.fee_status ?? "-"}
                            {toNum(entry?.total_due) ? ` (₹${entry.total_due} due)` : ""}
                        </span>
                        {avgPct !== null && (
                            <span
                                className={`badge bg-${gradeColor(getGrade(avgPct))}-subtle text-${gradeColor(getGrade(avgPct))}`}
                            >
                                <i className="ri-percent-line me-1"></i>
                                Avg {avgPct.toFixed(1)}%
                            </span>
                        )}
                       {hasAccess("/student/batch/mark/print") && (
                                <button
                                    className="btn btn-info"
                                    onClick={() => printImg("PrintDiv")}
                                    title="Print"
                                >
                                    <i className="ri-printer-line"></i>
                                </button>
                            )}
                    </div>
                </div>

                <div className="table-responsive">
                    {tests.length ? (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Test</th>
                                    <th>Date</th>
                                    <th>Max</th>
                                    <th>Obtained</th>
                                    <th>%</th>
                                    <th>Grade</th>
                                    <th>Rank</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tests.map((t) => (

                                    <TestRow
                                        key={t.id}
                                        test={t}
                                        isOpen={openTestId === t.id}
                                        onToggle={onToggleTest}
                                        grading={grading}
                                    />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-center text-muted py-4">
                            <i className="ri-file-list-3-line me-2"></i>
                            No tests conducted yet for this batch.
                        </div>
                    )}
                </div>

                {latestRank !== null && (
                    <div className="card-footer bg-white text-muted small">
                        <i className="ri-trophy-line me-1 text-warning"></i>
                        Latest rank: {latestRank}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------- main component ---------- */

export default function MarkReportV2({ id }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [openTestId, setOpenTestId] = useState(null);
    const [grading, setGrading] = useState([]);

    const getStudentDetailsMain = async (student_id) => {
        setLoading(true);
        try {
            const response = await studentDetailsMarkReport(student_id);
            if (response.data.status) {
                setData(response?.data?.data);
                setGrading(response?.data?.grading ?? []);

            } else {
                SweetAlertShow("info", "Warning", response?.data?.message);
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ??
                error?.response?.message ??
                error?.message,
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getStudentDetailsMain(id);
    }, [id]);

    const batches = data?.student_batch_n_fees ?? [];

    const overall = useMemo(() => {
        const allEvaluated = batches
            .flatMap((b) => b?.tests ?? [])
            .map((t) => getTestResult(t))
            .filter((r) => r.status === "evaluated");

        const sumObtained = allEvaluated.reduce((a, r) => a + (r.obtained ?? 0), 0);
        const sumTotal = allEvaluated.reduce((a, r) => a + (r.totalMarks ?? 0), 0);
        const pct = sumTotal ? (sumObtained / sumTotal) * 100 : null;
        const grade = getGrade(pct, grading);
        const latestRank =
            [...allEvaluated].reverse().find((r) => r.rank !== null)?.rank ?? null;

        return {
            pct,
            grade,
            testsTaken: allEvaluated.length,
            totalTests: batches.flatMap((b) => b?.tests ?? []).length,
            latestRank,
        };
    }, [batches, grading]);

    const handleToggleTest = (testId) => {
        setOpenTestId((prev) => (prev === testId ? null : testId));
    };

    return (
        <div className="row g-3">
            {loading ? (
                <SummarySkeleton />
            ) : (
                <>
                    <div className="col-lg-3 col-md-6">
                        <div className="simple-card border-success h-100">
                            <small className="text-muted fw-bold">
                                <i className="ri-percent-line me-1"></i>
                                Overall Percentage
                            </small>

                            <h2 className="text-success m-0 mt-2">
                                {overall.pct !== null
                                    ? `${overall.pct.toFixed(2)}%`
                                    : "-"}
                            </h2>
                        </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                        <div className="simple-card border-purple h-100">
                            <small className="text-muted fw-bold">
                                <i className="ri-medal-line me-1"></i>
                                Overall Grade
                            </small>

                            <h2 className="text-purple m-0 mt-2">
                                {overall.grade}
                            </h2>
                        </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                        <div className="simple-card border-warning h-100">
                            <small className="text-muted fw-bold">
                                <i className="ri-trophy-line me-1"></i>
                                Latest Rank
                            </small>

                            <h2 className="text-warning m-0 mt-2">
                                {overall.latestRank ?? "-"}
                            </h2>
                        </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                        <div className="simple-card border-info h-100">
                            <small className="text-muted fw-bold">
                                <i className="ri-file-list-3-line me-1"></i>
                                Tests Evaluated
                            </small>

                            <h2 className="text-info m-0 mt-2">
                                {overall.testsTaken}/{overall.totalTests}
                            </h2>
                        </div>
                    </div>
                </>
            )}

            {loading ? (
                <>
                    <BatchSkeleton />
                    <BatchSkeleton />
                </>
            ) : batches.length ? (
                batches.map((entry) => (
                    <BatchCard
                        key={entry.id}
                        entry={entry}
                        openTestId={openTestId}
                        onToggleTest={handleToggleTest}
                        grading={grading}
                        data={data}

                    />
                ))
            ) : (
                <EmptyState
                    icon="ri-file-forbid-line"
                    title="No academic records found"
                    subtitle="This student isn't enrolled in any batch yet, or no tests have been recorded."
                />
            )}
        </div>
    );
}
